// AdminManageSeasons: Admin UI for add/update/delete seasons
import React, { useEffect, useState } from "react";
import {
	Box,
	Typography,
	Button,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	TextField,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	CircularProgress,
} from "@mui/material";
import supabase from "../../../supabaseClient";

export default function AdminManageSeasons() {
	const [seasons, setSeasons] = useState([]);
	const [loading, setLoading] = useState(true);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editSeason, setEditSeason] = useState(null);
	const [form, setForm] = useState({
		name: "",
		preorder_open: "",
		preorder_close: "",
		fulfillment_start: "",
		fulfillment_end: "",
		notes: "",
	});
	const [saveStatus, setSaveStatus] = useState("");

	useEffect(() => {
		fetchSeasons();
	}, []);

	const fetchSeasons = async () => {
		setLoading(true);
		const { data } = await supabase
			.from("seasons")
			.select("*")
			.order("preorder_open", { ascending: true });
		setSeasons(data || []);
		setLoading(false);
	};

	const handleOpen = (season = null) => {
		setEditSeason(season);
		setForm(
			season
				? {
						...season,
						preorder_open: season.preorder_open
							? season.preorder_open.slice(0, 10)
							: "",
						preorder_close: season.preorder_close
							? season.preorder_close.slice(0, 10)
							: "",
						fulfillment_start: season.fulfillment_start
							? season.fulfillment_start.slice(0, 10)
							: "",
						fulfillment_end: season.fulfillment_end
							? season.fulfillment_end.slice(0, 10)
							: "",
					}
				: {
						name: "",
						preorder_open: "",
						preorder_close: "",
						fulfillment_start: "",
						fulfillment_end: "",
						notes: "",
					},
		);
		setDialogOpen(true);
	};

	const handleClose = () => {
		setDialogOpen(false);
		setEditSeason(null);
	};

	const handleChange = (e) => {
		setForm({ ...form, [e.target.name]: e.target.value });
	};

	const handleSave = async () => {
		setSaveStatus("");
		setLoading(true);
		let result;

		// Convert date fields to ISO 8601 format
		const formattedForm = {
			...form,
			preorder_open: form.preorder_open
				? new Date(form.preorder_open).toISOString()
				: null,
			preorder_close: form.preorder_close
				? new Date(form.preorder_close).toISOString()
				: null,
			fulfillment_start: form.fulfillment_start
				? new Date(form.fulfillment_start).toISOString()
				: null,
			fulfillment_end: form.fulfillment_end
				? new Date(form.fulfillment_end).toISOString()
				: null,
		};

		try {
			if (editSeason) {
				result = await supabase
					.from("seasons")
					.update(formattedForm)
					.eq("id", editSeason.id);
			} else {
				result = await supabase.from("seasons").insert([formattedForm]);
			}
			if (result.error) {
				setSaveStatus("error");
			} else {
				setSaveStatus("success");
				await fetchSeasons();
				// Optionally clear form if adding new
				if (!editSeason)
					setForm({
						name: "",
						preorder_open: "",
						preorder_close: "",
						fulfillment_start: "",
						fulfillment_end: "",
						notes: "",
					});
				// Close modal after success
				setTimeout(() => {
					setDialogOpen(false);
					setEditSeason(null);
					setSaveStatus("");
				}, 800);
			}
		} catch (err) {
			setSaveStatus("error");
		}
		setLoading(false);
	};

	const handleDelete = async (id) => {
		setLoading(true);
		await supabase.from("seasons").delete().eq("id", id);
		await fetchSeasons();
		setLoading(false);
	};

	return (
		<Box sx={{ mx: "auto", width: "100%" }}>
			<Typography variant="h5" gutterBottom>
				Manage Seasons
			</Typography>
			<Button
				variant="contained"
				color="primary"
				onClick={() => handleOpen()}
				sx={{ mb: 2 }}
			>
				Add New Season
			</Button>
			{loading ? (
				<CircularProgress />
			) : (
				<Box sx={{ width: "100%", overflowX: "auto" }}>
					<Table size="small" sx={{ minWidth: 500, width: "100%" }}>
						<TableHead>
							<TableRow>
								<TableCell sx={{ minWidth: 100 }}>Name</TableCell>
								<TableCell sx={{ minWidth: 120 }}>Preorder Open</TableCell>
								<TableCell sx={{ minWidth: 120 }}>Preorder Close</TableCell>
								<TableCell sx={{ minWidth: 120 }}>Fulfillment Start</TableCell>
								<TableCell sx={{ minWidth: 120 }}>Fulfillment End</TableCell>
								<TableCell sx={{ minWidth: 120 }}>Notes</TableCell>
								<TableCell sx={{ minWidth: 80 }}>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{seasons.map((season) => (
								<TableRow key={season.id}>
									<TableCell>{season.name}</TableCell>
									<TableCell>{season.preorder_open?.slice(0, 10)}</TableCell>
									<TableCell>{season.preorder_close?.slice(0, 10)}</TableCell>
									<TableCell>
										{season.fulfillment_start?.slice(0, 10)}
									</TableCell>
									<TableCell>{season.fulfillment_end?.slice(0, 10)}</TableCell>
									<TableCell>{season.notes}</TableCell>
									<TableCell>
										<Button size="small" onClick={() => handleOpen(season)}>
											Edit
										</Button>
										<Button
											size="small"
											color="error"
											onClick={() => handleDelete(season.id)}
										>
											Delete
										</Button>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Box>
			)}
			<Dialog
				open={dialogOpen}
				onClose={handleClose}
				fullWidth
				maxWidth="sm"
				sx={{ "& .MuiDialog-paper": { width: "100%", maxWidth: 400 } }}
			>
				<DialogTitle>{editSeason ? "Edit Season" : "Add Season"}</DialogTitle>
				<DialogContent sx={{ p: 1 }}>
					<TextField
						label="Name"
						name="name"
						value={form.name}
						onChange={handleChange}
						fullWidth
						margin="normal"
						size="small"
					/>
					<TextField
						label="Preorder Open"
						name="preorder_open"
						value={form.preorder_open}
						onChange={handleChange}
						type="date"
						fullWidth
						margin="normal"
						InputLabelProps={{ shrink: true }}
						size="small"
					/>
					<TextField
						label="Preorder Close"
						name="preorder_close"
						value={form.preorder_close}
						onChange={handleChange}
						type="date"
						fullWidth
						margin="normal"
						InputLabelProps={{ shrink: true }}
						size="small"
					/>
					<TextField
						label="Fulfillment Start"
						name="fulfillment_start"
						value={form.fulfillment_start}
						onChange={handleChange}
						type="date"
						fullWidth
						margin="normal"
						InputLabelProps={{ shrink: true }}
						size="small"
					/>
					<TextField
						label="Fulfillment End"
						name="fulfillment_end"
						value={form.fulfillment_end}
						onChange={handleChange}
						type="date"
						fullWidth
						margin="normal"
						InputLabelProps={{ shrink: true }}
						size="small"
					/>
					<TextField
						label="Notes"
						name="notes"
						value={form.notes}
						onChange={handleChange}
						fullWidth
						margin="normal"
						size="small"
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} disabled={loading}>
						Cancel
					</Button>
					<Button
						onClick={handleSave}
						variant="contained"
						color="primary"
						disabled={loading}
					>
						{loading ? <CircularProgress size={20} /> : "Save"}
					</Button>
				</DialogActions>
				{saveStatus === "success" && (
					<Box p={2} color="success.main">
						Season saved successfully.
					</Box>
				)}
				{saveStatus === "error" && (
					<Box p={2} color="error.main">
						Error saving season. Please try again.
					</Box>
				)}
			</Dialog>
		</Box>
	);
}
